import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  FerrisWheel,
  Fish,
  FlaskConical,
  Castle,
  Ship,
  Sailboat,
  Crown,
  TreePine,
  Puzzle,
  Baby,
  Bike,
  Bus,
  CloudRain,
  Footprints,
  UtensilsCrossed,
  
  Waves,
  RollerCoaster,
} from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand, GygTivoliCard, TIVOLI_TICKET_URL } from "@/components/GetYourGuide";

const HERO_IMG =
  "https://images.unsplash.com/photo-1646210201164-4cc951b59bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjb3BlbmhhZ2VuJTIwdGl2b2xpfGVufDB8fHx8MTc4NzA1NzI2NHww&ixlib=rb-4.1.0&q=85";

// The famous, paid, big-day-out attractions
const BIG_HIT = [
  {
    icon: FerrisWheel,
    title: "Tivoli Gardens",
    ticket: TIVOLI_TICKET_URL,
    body: "The 1843 amusement park in the middle of the city — gentle rides for small kids, proper roller coasters for big ones, peacocks on the lawns and thousands of lights after dark.",
    meta: "Half day · Apr–Dec seasons · Ride pass extra",
    address: "Vesterbrogade 3, 1630 København V",
  },
  {
    icon: Fish,
    title: "Den Blå Planet (The Blue Planet)",
    body: "Northern Europe's largest aquarium — sharks gliding over your head in the ocean tunnel, rays you can watch for hours, and an easy metro ride out on Amager.",
    meta: "~2–3h · Open daily year-round",
    address: "Jacob Fortlingsvej 1, 2770 Kastrup",
    link: { label: "The Amager area guide", to: "/areas/amager" },
  },
  {
    icon: FlaskConical,
    title: "Experimentarium",
    ticket: "https://gyg.me/14p7Y8sH",
    body: "A whole building of hands-on science in Hellerup — giant soap bubbles, illusions, water play and experiments. The single best rainy-day rescue with kids.",
    meta: "2–4h · Open daily 09:30–17:00",
    address: "Tuborg Havnevej 7, 2900 Hellerup",
  },
  {
    icon: Mark,
    title: "Copenhagen Zoo",
    ticket: "https://gyg.me/W3Senqr2",
    body: "Pandas, elephants, a polar bear that swims overhead in the Arctic Ring, a big playground and the historic wooden tower. A full family day out west in Frederiksberg.",
    meta: "4–5h · Open daily, closes early in winter",
    address: "Roskildevej 32, 2000 Frederiksberg",
    link: { label: "The Frederiksberg area guide", to: "/areas/frederiksberg" },
  },
  {
    icon: RollerCoaster,
    title: "Bakken",
    body: "The world's oldest amusement park (since 1583!) inside the Dyrehaven deer park — free to enter, pay per ride, and 2,000 free-roaming deer in the forest around it.",
    meta: "4–6h · Apr–Sep · Free entry",
    address: "Dyrehavsbakken, 2930 Klampenborg",
    link: { label: "The Bakken day trip guide", to: "/trips/bakken" },
  },
];

// Boats — their own category
const BOATS = [
  {
    icon: Ship,
    title: "Canal boat tour",
    ticket: "https://gyg.me/d4it0SF2",
    body: "The easiest hour with kids in Copenhagen: everyone sits, the city sails past, and there's something to point at every minute. Boats leave straight from Nyhavn.",
    meta: "~1h · All year · Kids usually half price",
    address: "Departs Nyhavn, 1051 København K",
  },
  {
    icon: Sailboat,
    title: "GoBoat",
    ticket: "https://gyg.me/6t5D3Rvi",
    body: "Captain your own little picnic boat — no licence needed, room for 8, and kids love being passengers with a snack table in the middle. From Islands Brygge.",
    meta: "2–3h · Mar–Oct · Book ahead in summer",
    address: "Islands Brygge 10, 2300 København S",
    link: { label: "The Islands Brygge area guide", to: "/areas/islands-brygge" },
  },
];

// Free (or nearly free) local fun
const FREE_FUN = [
  {
    icon: Puzzle,
    title: "The Children's Museum",
    body: "Inside the National Museum: a whole kids' universe where they can dress up, climb aboard a ship, cook in an old kitchen and touch everything.",
    meta: "1–2h · Free with a paying adult · Closes 16:30",
    address: "Ny Vestergade 10, 1471 København K",
    link: { label: "More rainy-day saves", to: "/rainy-day" },
  },
  {
    icon: Castle,
    title: "The Tower Playground",
    body: "Fælledparken's famous playground where the climbing towers are mini versions of Copenhagen's real towers — Rundetårn included. Locals' favourite, completely free.",
    meta: "As long as they last · Free",
    address: "Fælledparken, 2100 København Ø",
    link: { label: "The Østerbro area guide", to: "/areas/osterbro" },
  },
  {
    icon: Waves,
    title: "Konditaget Lüders",
    body: "A rooftop playground on top of a Nordhavn parking house — trampolines, swings and climbing frames with the harbour spread out below. Free, and the view is for the grown-ups.",
    meta: "~1h · Free",
    address: "Helsinkigade 30, 2150 Nordhavn",
    link: { label: "The Nordhavn area guide", to: "/areas/nordhavn" },
  },
  {
    icon: Crown,
    title: "Changing of the guard",
    body: "Soldiers in bearskin hats march through the city at noon — free street theatre that kids actually watch. Arrive at Amalienborg a little before 12:00.",
    meta: "~30 min · Free · Daily at noon",
    address: "Amalienborg Slotsplads, 1257 København K",
    link: { label: "The Indre By area guide", to: "/areas/indre-by" },
  },
  {
    icon: TreePine,
    title: "Kongens Have",
    body: "The King's Garden: a big adventure playground, lawns made for picnics, and Rosenborg Castle as the backdrop. The city's easiest family afternoon.",
    meta: "Free",
    address: "Øster Voldgade 4A, 1350 København K",
  },
  {
    icon: Waves,
    title: "Harbour swims",
    body: "In summer the harbour itself is the pool — Islands Brygge has dedicated kids' basins, shallow and watched. June–August.",
    meta: "Jun–Aug · Free",
    link: { label: "The harbour swimming guide", to: "/harbour-swimming" },
  },
];

// Practical stuff parents actually ask
const GOOD_TO_KNOW = [
  {
    icon: Bus,
    title: "Kids ride free",
    body: "An adult can bring 2 children under 12 along for free on buses, the metro and S-trains. No ticket needed for the kids — just hop on.",
    link: { label: "The transport guide", to: "/transport" },
  },
  {
    icon: Bike,
    title: "Rent a cargo bike",
    body: "The local family vehicle: a Christiania-style box bike carries two kids plus the picnic. Rental shops are all over the centre — and the bike lanes are wide and safe.",
    link: { label: "Biking in Copenhagen", to: "/biking" },
  },
  {
    icon: Baby,
    title: "Changing & nursing",
    body: "Department stores Magasin and Illum have proper baby rooms, as do the big museums. Danes are relaxed — nursing anywhere is completely normal.",
  },
  {
    icon: Footprints,
    title: "Strollers roll easy",
    body: "The metro has level access and lifts everywhere, buses kneel, and the city is flat with smooth kerbs. Fold the pram on busy buses at rush hour.",
  },
  {
    icon: CloudRain,
    title: "Rain plan ready",
    body: "It rains ~170 days a year — pack the rain suit and keep Experimentarium, the Blue Planet and the Children's Museum up your sleeve.",
    link: { label: "The rainy-day guide", to: "/rainy-day" },
  },
  {
    icon: UtensilsCrossed,
    title: "Eating with kids",
    body: "High chairs are normal almost everywhere. Food halls are the family cheat code: Torvehallerne, Tivoli Food Hall and Reffen let everyone pick something different. A pølsevogn hotdog or a snack from 7 ELEVEN fixes most emergencies.",
    link: { label: "The Danish food guide", to: "/danish-food" },
  },
];

const FAQ = [
  {
    q: "Is Copenhagen a good city for kids?",
    a: "One of Europe's best. It's flat, safe and compact, playgrounds are everywhere, restaurants expect children, and the metro runs 24/7 when someone falls asleep in the stroller. Danes build their daily life around kids — you'll fit right in.",
  },
  {
    q: "What are the best things to do in Copenhagen with toddlers?",
    a: "The Zoo's playgrounds and animals, the Children's Museum inside the National Museum (free), the Tower Playground in Fælledparken, a gentle canal boat tour, and Kongens Have for running free. Everything is close together, so naps are easy to plan around.",
  },
  {
    q: "Do kids travel free on public transport in Copenhagen?",
    a: "Yes — an adult with a valid ticket can bring 2 children under 12 along for free on buses, the metro and S-trains. It makes family transport remarkably cheap.",
  },
  {
    q: "What do you do in Copenhagen with kids when it rains?",
    a: "Experimentarium (hands-on science), Den Blå Planet (the aquarium), the Children's Museum at the National Museum and the Natural History Museum's dinosaurs are the big four. See the full rainy-day guide for more.",
  },
  {
    q: "Is the Copenhagen Card worth it for families?",
    a: "Often yes — each adult Copenhagen Card covers 2 children aged 3–11 for free, and it includes transport plus Tivoli, the Zoo, Experimentarium and the canal tour. Do the maths on your plans first.",
  },
  {
    q: "Which area is best to stay in with kids?",
    a: "Østerbro and Frederiksberg are calm, green and stroller-friendly with big parks. Indre By puts everything on your doorstep. See the where-to-stay guide for the full breakdown.",
  },
];

// Address chip → opens Google Maps
const Addr = ({ a }) => {
  const slug = a.split(",")[0].toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <a
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`kids-addr-${slug}`}
      className="inline-flex items-center gap-1.5 mt-2 font-body text-xs text-black/50 hover:text-[#DF3226] underline underline-offset-4 decoration-transparent hover:decoration-[#DF3226]/50 transition-colors"
    >
      <MapPin size={12} className="shrink-0 text-black/40" /> {a}
    </a>
  );
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function KidsPage() {
  return (
    <main data-testid="kids-page" className="bg-[#F9F8F6] text-black font-body">
      <Seo
        title="Copenhagen with Kids: The Complete Family Guide"
        description="The best things to do in Copenhagen with kids — Tivoli, the Blue Planet aquarium, Experimentarium, free playgrounds, kid-friendly food and the transport tricks parents need."
        path="/copenhagen-with-kids"
        image="https://cphhighlights.com/planner/115c33e22770.webp"
      />
      <PageSchema
        name="Copenhagen with Kids: The Complete Family Guide"
        description="The best things to do in Copenhagen with kids — attractions, free playgrounds, rainy-day saves, eating and practical transport tips for families."
        path="/copenhagen-with-kids"
        crumb="Copenhagen with kids"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden pt-16 md:pt-20">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Tivoli Gardens amusement park lights in Copenhagen"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="kids-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-black shadow-brut hover:-translate-y-0.5 transition-all"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton title="Copenhagen with Kids — Family Guide" testid="kids-share" />
          </div>
          <p className="font-body font-800 uppercase tracking-[0.2em] text-white text-xs">
            The family guide
          </p>
          <h1 className="font-display font-900 tracking-tighter leading-[0.92] text-4xl sm:text-5xl lg:text-7xl mt-4 max-w-4xl">
            Copenhagen with kids. <span className="font-editorial italic font-600">Easy mode</span>
          </h1>
          <p className="font-body text-white/85 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">
            Fairy-tale castles, hands-on science, an ocean tunnel, free playgrounds shaped like the city's
            own towers — and a transport system where your kids ride free. Copenhagen is built for families.
          </p>
        </div>
      </section>

      {/* the big hitters */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">THE BIG DAYS OUT</p>
            <h2 className="font-display font-900 tracking-tight text-3xl md:text-5xl mb-8">
              The ones they'll talk about <span className="font-editorial italic font-600">all year</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BIG_HIT.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05} y={30}>
                <div
                  data-testid={`kids-big-${i}`}
                  className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <s.icon size={22} className="text-[#DF3226]" />
                  <h3 className="font-display font-900 tracking-tight text-lg mt-3 leading-tight">
                    {s.ticket ? (
                      <a
                        href={s.ticket}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid={`kids-big-ticket-${i}`}
                        className="underline decoration-[#DF3226]/40 underline-offset-4 hover: transition-colors"
                      >
                        {s.title}
                      </a>
                    ) : (
                      s.title
                    )}
                  </h3>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{s.body}</p>
                  <p className="font-body font-700 text-[11px] tracking-widest text-[#E8590C] mt-4">{s.meta.toUpperCase()}</p>
                  {s.address && <Addr a={s.address} />}
                  {s.link && (
                    <Link
                      to={s.link.to}
                      data-testid={`kids-big-link-${i}`}
                      className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      {s.link.label}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-6 max-w-md">
              <GygTivoliCard testid="kids-tivoli-ticket-card" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* boats */}
      <section className="pb-12 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">ON THE WATER</p>
            <h2 className="font-display font-900 tracking-tight text-3xl md:text-5xl mb-8">
              Boat trips <span className="font-editorial italic font-600">they'll beg to repeat</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {BOATS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.05} y={30}>
                <div
                  data-testid={`kids-boat-${i}`}
                  className="h-full bg-[#F9F8F6] border border-zinc-200 shadow-brut rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <s.icon size={22} className="text-[#DF3226]" />
                  <h3 className="font-display font-900 tracking-tight text-lg mt-3 leading-tight">
                    {s.ticket ? (
                      <a
                        href={s.ticket}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid={`kids-boat-ticket-${i}`}
                        className="underline decoration-[#DF3226]/40 underline-offset-4 hover: transition-colors"
                      >
                        {s.title}
                      </a>
                    ) : (
                      s.title
                    )}
                  </h3>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{s.body}</p>
                  <p className="font-body font-700 text-[11px] tracking-widest text-[#E8590C] mt-4">{s.meta.toUpperCase()}</p>
                  {s.address && <Addr a={s.address} />}
                  {s.link && (
                    <Link
                      to={s.link.to}
                      data-testid={`kids-boat-link-${i}`}
                      className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      {s.link.label}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* planner shortcut */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <Link
              to="/planner"
              data-testid="kids-planner-strip"
              className="flex items-center justify-between gap-4 bg-[#13141A] text-white rounded-xl px-6 md:px-8 py-5 md:py-6 shadow-brut hover:-translate-y-0.5 hover:shadow-brut-lg transition-all"
            >
              <div>
                <p className="font-display font-900 tracking-tight text-lg md:text-2xl">Putting a day together?</p>
                <p className="font-body text-sm text-white/75 mt-1">Pick these in the planner and it groups them into days that actually work with kids.</p>
              </div>
              <ArrowRight size={22} className="shrink-0" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* free fun */}
      <section className="py-12 md:py-20 bg-white border-y border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">FREE (OR NEARLY)</p>
            <h2 className="font-display font-900 tracking-tight text-3xl md:text-5xl mb-8">
              Local fun that costs <span className="font-editorial italic font-600">nothing</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FREE_FUN.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05} y={30}>
                <div
                  data-testid={`kids-free-${i}`}
                  className="h-full bg-[#F9F8F6] border border-zinc-200 shadow-brut rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <s.icon size={22} className="text-[#DF3226]" />
                  <h3 className="font-display font-900 tracking-tight text-lg mt-3 leading-tight">{s.title}</h3>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{s.body}</p>
                  <p className="font-body font-700 text-[11px] tracking-widest text-[#E8590C] mt-4">{s.meta.toUpperCase()}</p>
                  {s.address && <Addr a={s.address} />}
                  {s.link && (
                    <Link
                      to={s.link.to}
                      data-testid={`kids-free-link-${i}`}
                      className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      {s.link.label}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* good to know */}
      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">GOOD TO KNOW</p>
            <h2 className="font-display font-900 tracking-tight text-3xl md:text-5xl mb-8">
              The stuff parents <span className="font-editorial italic font-600">actually ask</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {GOOD_TO_KNOW.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05} y={30}>
                <div
                  data-testid={`kids-tip-${i}`}
                  className="h-full bg-white border border-zinc-200 shadow-brut rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  <s.icon size={22} className="text-[#DF3226]" />
                  <h3 className="font-display font-900 tracking-tight text-lg mt-3 leading-tight">{s.title}</h3>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed">{s.body}</p>
                  {s.link && (
                    <Link
                      to={s.link.to}
                      data-testid={`kids-tip-link-${i}`}
                      className="inline-block mt-3 font-body font-700 text-xs text-[#DF3226] underline underline-offset-4 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
                    >
                      {s.link.label}
                    </Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* planner CTA */}
      <section data-testid="kids-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24 text-center">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">ONE PLAN, EVERYONE HAPPY</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl mt-4 max-w-2xl mx-auto">
              Mix the kid stuff with <span className="font-editorial italic font-600">your stuff</span>
            </h2>
            <p className="font-body text-base text-white/80 max-w-xl mx-auto mt-5">
              The Zoo in the morning, a bakery queue for you, Kongens Have playground after lunch —
              the planner groups it all into days that work.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/copenhagen-with-kids-itinerary"
                data-testid="kids-cta-itinerary"
                className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
              >
                See the 3-day family plan
                <ArrowRight size={20} className="shrink-0" />
              </Link>
              <Link
                to="/planner"
                data-testid="kids-cta-planner"
                className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
              >
                Build your own
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <GygTourBand tourId="965634" label="SKIP THE QUEUE: EXPERIMENTARIUM TICKETS" />

      <ExploreLinks
        testid="explore-kids"
        links={[
          { label: "A ready-made plan — 3 days in Copenhagen with kids", to: "/copenhagen-with-kids-itinerary" },
          { label: "Rain-proof the trip — the rainy-day guide", to: "/rainy-day" },
          { label: "Where to stay with kids — straight to the lower-budget hotels", to: "/stay#budget-hotels" },
          { label: "Day trips: castles, Vikings & Sweden", to: "/trips" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section className="pb-12 md:pb-20 border-t border-dotted border-zinc-300 pt-12 md:pt-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-6">
            Family questions, answered
          </h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details
                key={f.q}
                data-testid={`kids-faq-${i}`}
                className="group bg-white border border-zinc-200 rounded-xl px-5 py-4"
              >
                <summary className="cursor-pointer list-none font-display font-800 text-base md:text-lg flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[#DF3226] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Copenhagen with Kids — Family Guide" />
    </main>
  );
}
